/**
 * Brand icons — Phosphor Icons (`regular` weight per Sunlower brand identity).
 * Import from here instead of lucide-react.
 */
import type { Icon, IconProps } from "@phosphor-icons/react";
import {
  ArrowClockwise as PhArrowClockwise,
  ArrowCounterClockwise as PhArrowCounterClockwise,
  ArrowLeft as PhArrowLeft,
  ArrowRight as PhArrowRight,
  Bell as PhBell,
  BellRinging as PhBellRinging,
  Bluetooth as PhBluetooth,
  Calendar as PhCalendar,
  CaretDown as PhCaretDown,
  CaretLeft as PhCaretLeft,
  CaretRight as PhCaretRight,
  CaretUp as PhCaretUp,
  CaretUpDown as PhCaretUpDown,
  ChartBar as PhChartBar,
  Check as PhCheck,
  CheckCircle as PhCheckCircle,
  Checks as PhChecks,
  Circle as PhCircle,
  CircleNotch as PhCircleNotch,
  Columns as PhColumns,
  Command as PhCommand,
  Compass as PhCompass,
  Copy as PhCopy,
  Diamond as PhDiamond,
  DotsSixVertical as PhDotsSixVertical,
  DotsThree as PhDotsThree,
  Download as PhDownload,
  Eye as PhEye,
  EyeSlash as PhEyeSlash,
  Faders as PhFaders,
  Flower as PhFlower,
  Gear as PhGear,
  Gift as PhGift,
  Hamburger as PhHamburger,
  Image as PhImage,
  Info as PhInfo,
  MagnifyingGlass as PhMagnifyingGlass,
  MapPin as PhMapPin,
  Minus as PhMinus,
  Monitor as PhMonitor,
  Moon as PhMoon,
  Package as PhPackage,
  Palette as PhPalette,
  PencilSimple as PhPencilSimple,
  Phone as PhPhone,
  Plus as PhPlus,
  Prohibit as PhProhibit,
  Question as PhQuestion,
  Receipt as PhReceipt,
  Scissors as PhScissors,
  Scroll as PhScroll,
  ShieldCheck as PhShieldCheck,
  ShoppingBag as PhShoppingBag,
  Sidebar as PhSidebar,
  SignIn as PhSignIn,
  SignOut as PhSignOut,
  Sparkle as PhSparkle,
  SplitVertical as PhSplitVertical,
  SquaresFour as PhSquaresFour,
  Storefront as PhStorefront,
  Sun as PhSun,
  Trash as PhTrash,
  TrendDown as PhTrendDown,
  TrendUp as PhTrendUp,
  Truck as PhTruck,
  Upload as PhUpload,
  User as PhUser,
  UserGear as PhUserGear,
  Users as PhUsers,
  Warning as PhWarning,
  Waveform as PhWaveform,
  X as PhX,
  XCircle as PhXCircle,
  Yarn as PhYarn,
} from "@phosphor-icons/react";

/** @deprecated Use `Icon` from `@/components/icons` */
export type { Icon, Icon as LucideIcon, IconProps };

function withRegularWeight(PhosphorIcon: Icon): Icon {
  const Wrapped = ({ weight = "regular", ...props }: IconProps) => (
    <PhosphorIcon weight={weight} {...props} />
  );
  Wrapped.displayName = PhosphorIcon.displayName ?? "Icon";
  return Wrapped as Icon;
}

export const ArrowLeftIcon = withRegularWeight(PhArrowLeft);
export const ArrowRightIcon = withRegularWeight(PhArrowRight);
export const BanIcon = withRegularWeight(PhProhibit);
export const BellRing = withRegularWeight(PhBellRinging);
export const Bluetooth = withRegularWeight(PhBluetooth);
export const CalendarIcon = withRegularWeight(PhCalendar);
export const CheckCheck = withRegularWeight(PhChecks);
export const CheckIcon = withRegularWeight(PhCheck);
export const ChevronDownIcon = withRegularWeight(PhCaretDown);
export const ChevronLeftIcon = withRegularWeight(PhCaretLeft);
export const ChevronRightIcon = withRegularWeight(PhCaretRight);
export const ChevronUpIcon = withRegularWeight(PhCaretUp);
export const CircleCheck = withRegularWeight(PhCheckCircle);
export const CircleCheckIcon = withRegularWeight(PhCheckCircle);
export const CircleHelp = withRegularWeight(PhQuestion);
export const CircleIcon = withRegularWeight(PhCircle);
export const EditIcon = withRegularWeight(PhPencilSimple);
export const EyeIcon = withRegularWeight(PhEye);
export const EyeOffIcon = withRegularWeight(PhEyeSlash);
export const GripVerticalIcon = withRegularWeight(PhDotsSixVertical);
export const HelpCircleIcon = withRegularWeight(PhQuestion);
export const ImageIcon = withRegularWeight(PhImage);
export const InfoIcon = withRegularWeight(PhInfo);
export const Loader2 = withRegularWeight(PhCircleNotch);
export const Loader2Icon = withRegularWeight(PhCircleNotch);
export const LogIn = withRegularWeight(PhSignIn);
export const LogOut = withRegularWeight(PhSignOut);
export const Menu = withRegularWeight(PhHamburger);
export const MinusIcon = withRegularWeight(PhMinus);
export const MoonIcon = withRegularWeight(PhMoon);
export const MoreHorizontal = withRegularWeight(PhDotsThree);
export const MoreHorizontalIcon = withRegularWeight(PhDotsThree);
export const OctagonXIcon = withRegularWeight(PhXCircle);
export const PanelLeftIcon = withRegularWeight(PhSidebar);
export const PlusIcon = withRegularWeight(PhPlus);
export const SearchIcon = withRegularWeight(PhMagnifyingGlass);
export const Settings = withRegularWeight(PhGear);
export const SunIcon = withRegularWeight(PhSun);
export const TrashIcon = withRegularWeight(PhTrash);
export const Trash = withRegularWeight(PhTrash);
export const Minus = withRegularWeight(PhMinus);
export const UserIcon = withRegularWeight(PhUser);
export const TriangleAlertIcon = withRegularWeight(PhWarning);
export const UserRound = withRegularWeight(PhUser);
export const XIcon = withRegularWeight(PhX);

// Lucide name aliases
export const AlertTriangle = withRegularWeight(PhWarning);
export const AudioWaveform = withRegularWeight(PhWaveform);
export const ArrowLeft = ArrowLeftIcon;
export const ArrowRight = ArrowRightIcon;
export const Bell = withRegularWeight(PhBell);
export const Check = withRegularWeight(PhCheck);
export const ChevronDown = withRegularWeight(PhCaretDown);
export const ChevronRight = withRegularWeight(PhCaretRight);
export const ChevronUp = withRegularWeight(PhCaretUp);
export const ChevronsUpDown = withRegularWeight(PhCaretUpDown);
export const Compass = withRegularWeight(PhCompass);
export const Diamond = withRegularWeight(PhDiamond);
export const ChartBar = withRegularWeight(PhChartBar);
export const Copy = withRegularWeight(PhCopy);
export const Download = withRegularWeight(PhDownload);
export const Flower = withRegularWeight(PhFlower);
export const Gift = withRegularWeight(PhGift);
export const SlidersHorizontal = withRegularWeight(PhFaders);
export const GalleryVerticalEnd = withRegularWeight(PhSplitVertical);
export const SplitVertical = withRegularWeight(PhSplitVertical);
export const LayoutDashboard = withRegularWeight(PhSquaresFour);
export const Monitor = withRegularWeight(PhMonitor);
export const Moon = withRegularWeight(PhMoon);
export const PanelsTopLeft = withRegularWeight(PhColumns);
export const Plus = withRegularWeight(PhPlus);
export const RefreshCw = withRegularWeight(PhArrowClockwise);
export const RotateCcw = withRegularWeight(PhArrowCounterClockwise);
export const ShieldCheck = withRegularWeight(PhShieldCheck);
export const Sun = withRegularWeight(PhSun);
export const Sparkle = withRegularWeight(PhSparkle);
export const Trash2 = withRegularWeight(PhTrash);
export const TrendingDown = withRegularWeight(PhTrendDown);
export const TrendingUp = withRegularWeight(PhTrendUp);
export const Upload = withRegularWeight(PhUpload);
export const UserCog = withRegularWeight(PhUserGear);
export const Users = withRegularWeight(PhUsers);
export const Command = withRegularWeight(PhCommand);
export const Package = withRegularWeight(PhPackage);
export const Palette = withRegularWeight(PhPalette);
export const Phone = withRegularWeight(PhPhone);
export const MapPin = withRegularWeight(PhMapPin);
export const Receipt = withRegularWeight(PhReceipt);
export const Scissors = withRegularWeight(PhScissors);
export const Scroll = withRegularWeight(PhScroll);
export const ShoppingBag = withRegularWeight(PhShoppingBag);
export const Storefront = withRegularWeight(PhStorefront);
export const Truck = withRegularWeight(PhTruck);
export const Yarn = withRegularWeight(PhYarn);
export const Info = withRegularWeight(PhInfo);
export const XCircle = withRegularWeight(PhXCircle);
